// src/app/admin/optimizationPipeline/component/UnitOptimizerParamHistoryPopup.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { LineChart, BarChart, Bar, Cell, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Tooltip as MuiTooltip, TextField, Checkbox, ListItemText, Chip, TableContainer, Table, TableHead, TableBody, TableCell, TableRow, Paper } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';
import format from 'date-fns/format';
import { sub } from 'date-fns';
import { MdShowChart, MdOutlineTableChart, MdBarChart } from 'react-icons/md';
import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';
import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

const intervalButtons = [{ label: 'All', unit: null }, { label: '1y', unit: { years: 1 } }, { label: '1m', unit: { months: 1 } }, { label: '1w', unit: { weeks: 1 } }, { label: '1d', unit: { days: 1 } }];

function isNumber(x) { return typeof x === 'number' && Number.isFinite(x); }
function toMs(v) { if (!v) return NaN; if (v instanceof Date) return v.getTime(); const t = new Date(v).getTime(); return Number.isFinite(t) ? t : NaN; }

function getByPath(obj, path) {
    if (!obj || typeof obj !== 'object' || !path) return undefined;
    const parts = String(path).split('.'); let cur = obj;
    for (const p of parts) { if (!cur || typeof cur !== 'object') return undefined; if (p === 'id') return undefined; cur = cur[p]; }
    return cur;
}

function normalizeNumber(v) { if (isNumber(v)) return v; if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v); if (typeof v === 'boolean') return v ? 1 : 0; return null; }
function colorForIndex(idx, count) { if (count <= 1) return '#252f3e'; return 'hsl(' + ((idx * 60) % 360) + ', 70%, 40%)'; }

function buildMinMaxDates(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return { minDate: null, maxDate: null };
    const sorted = [...rows].filter(Boolean).filter(r => r && r.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    if (!sorted.length) return { minDate: null, maxDate: null };
    const minDate = new Date(sorted[0].ts), maxDate = new Date(sorted[sorted.length - 1].ts);
    if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return { minDate: null, maxDate: null };
    return { minDate, maxDate };
}

export default function UnitOptimizerParamHistoryPopup(props) {
    const mainTheme = useSelector(selectMainTheme);
    const { unitOptimizerLogDataList } = useOptimizationPipeline();

    const [mode, setMode] = useState('diff');
    const [isChartView, setIsChartView] = useState(true);
    const [chartType, setChartType] = useState('bar'); // NEW: default bar

    const availableKeys = useMemo(() => Array.isArray(props.availableKeys) ? props.availableKeys.filter(Boolean) : [], [props.availableKeys]);

    const sortedRows = useMemo(() => {
        if (!Array.isArray(unitOptimizerLogDataList)) return [];
        return [...unitOptimizerLogDataList].filter(r => r && r.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [unitOptimizerLogDataList]);

    const { minDate, maxDate } = useMemo(() => buildMinMaxDates(sortedRows), [sortedRows]);

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedKeys, setSelectedKeys] = useState([]);

    useEffect(() => {
        if (!props.open) return;
        if (props.initialMode === 'base' || props.initialMode === 'best' || props.initialMode === 'diff') setMode(props.initialMode); else setMode('diff');
        if (minDate && maxDate) { setDateFrom(minDate); setDateTo(maxDate); } else { setDateFrom(null); setDateTo(null); }
        const seed = Array.isArray(props.defaultSelectedKeys) ? props.defaultSelectedKeys.filter(Boolean) : [];
        if (seed.length) { setSelectedKeys(seed); return; }
        setSelectedKeys(prev => { if (prev && prev.length) return prev; if (availableKeys.length) return [availableKeys[0]]; return []; });
    }, [props.open, props.defaultSelectedKeys, props.initialMode, minDate, maxDate, availableKeys]);

    const handleIntervalSelect = useCallback((unit) => {
        if (!minDate || !maxDate) return;
        if (!unit) { setDateFrom(minDate); setDateTo(maxDate); return; }
        const newFrom = sub(maxDate, unit);
        if (newFrom < minDate) { setDateFrom(minDate); setDateTo(maxDate); return; }
        setDateFrom(newFrom); setDateTo(maxDate);
    }, [minDate, maxDate]);

    const isIntervalAvailable = useCallback((unit) => {
        if (!minDate || !maxDate) return false;
        if (!unit) return true;
        return sub(maxDate, unit) >= minDate;
    }, [minDate, maxDate]);

    const IntervalButtons = () => (
        <div className='flex gap-4 items-center'>
            {intervalButtons.map(({ label, unit }) => (
                <MuiTooltip key={label} title={(unit ? 'Last ' : '') + label} arrow>
                    <span>
                        <IconButton size='small' onClick={() => handleIntervalSelect(unit)} disabled={!isIntervalAvailable(unit)}>
                            <span className='text-xs font-semibold p-4'>{label}</span>
                        </IconButton>
                    </span>
                </MuiTooltip>
            ))}

            <MuiTooltip title='Base / Best / Diff' arrow>
                <span className='flex items-center gap-2'>
                    <IconButton size='small' onClick={() => setMode('base')}><span className={'text-xs font-semibold p-4 ' + (mode === 'base' ? 'opacity-100' : 'opacity-60')}>Base</span></IconButton>
                    <IconButton size='small' onClick={() => setMode('best')}><span className={'text-xs font-semibold p-4 ' + (mode === 'best' ? 'opacity-100' : 'opacity-60')}>Best</span></IconButton>
                    <IconButton size='small' onClick={() => setMode('diff')}><span className={'text-xs font-semibold p-4 ' + (mode === 'diff' ? 'opacity-100' : 'opacity-60')}>Diff</span></IconButton>
                </span>
            </MuiTooltip>

            {/* NEW: Bar/Line icon toggle (like Chart/Table). Disabled in table view. */}
            <MuiTooltip title={chartType === 'bar' ? 'Line chart' : 'Bar chart'} arrow>
                <span>
                    <IconButton size='small' onClick={() => setChartType(t => (t === 'bar' ? 'line' : 'bar'))} disabled={!isChartView}>
                        {chartType === 'bar' ? <MdShowChart size={20} /> : <MdBarChart size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>

            <MuiTooltip title={isChartView ? 'Table view' : 'Chart view'} arrow>
                <span>
                    <IconButton size='small' onClick={() => setIsChartView(prev => !prev)}>
                        {isChartView ? <MdOutlineTableChart size={20} /> : <MdShowChart size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>
        </div>
    );

    const isDataAvailable = useMemo(() => selectedKeys.length > 0 && minDate && maxDate && dateFrom && dateTo, [selectedKeys, minDate, maxDate, dateFrom, dateTo]);

    const filteredData = useMemo(() => {
        if (!isDataAvailable) return [];
        const merged = {}, fromMs = toMs(dateFrom), toMsVal = toMs(dateTo);

        for (const row of sortedRows) {
            const t = toMs(row.ts);
            if (!Number.isFinite(t) || t < fromMs || t > toMsVal) continue;

            const ts = row.ts;
            if (!merged[ts]) merged[ts] = { ts };

            for (const key of selectedKeys) {
                const baseRaw = getByPath(row.base_state || {}, key);
                const bestRawDirect = getByPath(row.best_state || {}, key);
                const bestRaw = (bestRawDirect !== undefined) ? bestRawDirect : baseRaw;

                const base = normalizeNumber(baseRaw), best = normalizeNumber(bestRaw);
                let v = null;
                if (mode === 'base') v = base; else if (mode === 'best') v = best; else v = (base != null && best != null) ? (best - base) : null;
                merged[ts][key] = v;
            }
        }

        return Object.values(merged).filter(r => r && r.ts).sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [sortedRows, selectedKeys, dateFrom, dateTo, mode, isDataAvailable]);

    const getTooltip = ({ active, payload }) => {
        if (!(active && payload && payload.length)) return null;
        const p = payload[0] && payload[0].payload ? payload[0].payload : null;
        if (!p) return null;

        return (
            <div className='flex flex-col gap-1 bg-black opacity-80 p-8 text-white text-xs shadow-lg min-w-[220px] max-w-[340px]'>
                <div className='mb-2 font-semibold text-[11px] opacity-80'>{format(new Date(p.ts), 'yyyy.MM.dd.')}</div>
                {selectedKeys.map((k) => (<div key={k} className='font-medium'>{k}: {p[k] == null ? '-' : formatLabelValue(p[k], defaultLabelStyle())}</div>))}
            </div>
        );
    };

    // NEW: keep dialog size stable if there are available keys (even if nothing is selected yet)
    const hasStableLayout = availableKeys.length > 0;

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open={!!props.open} fullWidth maxWidth={hasStableLayout ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>
                    <div className={'flex items-center justify-between gap-32 px-32 ' + (hasStableLayout ? 'border-b pb-14' : '')}>
                        <div className='whitespace-nowrap'>{props.title || 'Parameter Timeseries'}</div>
                        <Button onClick={props.onClose}>Close</Button>
                    </div>

                    {availableKeys.length > 0 && (
                        <div className='w-full flex flex-col gap-8 shadow-inner px-20 pt-14'>
                            <div className='w-full flex flex-col gap-8 rounded-xl bg-grey-100 py-10 shadow px-16'>
                                <Autocomplete
                                    className='flex-1' multiple options={availableKeys} value={selectedKeys} onChange={(e, newValue) => setSelectedKeys(newValue)}
                                    disableCloseOnSelect getOptionLabel={(option) => option}
                                    renderOption={(option, { selected }) => {
                                        const parts = option.split('.'), parent = parts.length > 1 ? parts[0] : null, child = parts.length > 1 ? parts.slice(1).join('.') : option;
                                        return (<><Checkbox style={{ marginRight: 8 }} checked={selected} /><ListItemText primary={parent ? (<span><span className='font-semibold opacity-30'>{parent + '.'}</span>{child}</span>) : option} /></>);
                                    }}
                                    renderTags={(value, getTagProps) => {
                                        const count = value.length;
                                        return value.map((option, index) => {
                                            const col = colorForIndex(index, count);
                                            return (<Chip label={option} {...getTagProps({ index })} size='small' style={{ padding: '5px 5px', marginRight: 4, marginBottom: 4, fontSize: '12px', border: '2px solid ' + col }} />);
                                        });
                                    }}
                                    renderInput={(params) => {
                                        const { InputProps: extractedInputProps, inputProps: extractedInnerInputProps, ...filteredParams } = params;
                                        return (
                                            <TextField
                                                {...filteredParams} variant='outlined' label='Search and select' placeholder='Select keys' InputLabelProps={{ shrink: true }}
                                                InputProps={{ ...extractedInputProps, style: { minHeight: 40 }, inputProps: { ...extractedInnerInputProps, style: { padding: '10px 8px', fontSize: '13px', lineHeight: 1.5 } } }}
                                            />
                                        );
                                    }}
                                />

                                <div className='flex-1 flex items-center gap-16'>
                                    <DatePick className='flex-1' label='From' value={dateFrom} minDate={minDate} maxDate={maxDate} onChange={(v) => setDateFrom(v)} />
                                    <DatePick className='flex-1' label='To' value={dateTo} minDate={dateFrom || minDate} maxDate={maxDate} onChange={(v) => setDateTo(v)} />
                                    <IntervalButtons />
                                </div>
                            </div>
                        </div>
                    )}
                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: hasStableLayout ? '520px' : 'auto' }}>
                    {availableKeys.length === 0 && <div className='text-center'>No keys available.</div>}
                    {availableKeys.length > 0 && selectedKeys.length === 0 && <div className='text-center'>Select at least one parameter.</div>}
                    {availableKeys.length > 0 && selectedKeys.length > 0 && filteredData.length === 0 && <div className='text-center'>No data available.</div>}

                    {filteredData.length > 0 && (
                        <div className='w-full flex-1' style={{ minHeight: 400 }}>
                            {isChartView ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                    {chartType === 'bar' ? (
                                        <BarChart data={filteredData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <Tooltip content={getTooltip} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />
                                            {selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return (
                                                    <Bar key={key} dataKey={key} radius={[3, 3, 0, 0]} isAnimationActive={false}>
                                                        {filteredData.map((r, i) => (<Cell key={key + '_' + (r.ts || i)} fill={col} />))}
                                                    </Bar>
                                                );
                                            })}
                                        </BarChart>
                                    ) : (
                                        <LineChart data={filteredData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <Tooltip content={getTooltip} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />
                                            {selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return <Line key={key} type='monotone' dataKey={key} stroke={col} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />;
                                            })}
                                        </LineChart>
                                    )}
                                </ResponsiveContainer>
                            ) : (
                                <DataTable data={filteredData} selectedKeys={selectedKeys} />
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}

function DataTable({ data, selectedKeys }) {
    return (
        <TableContainer component={Paper} style={{ height: '100%' }}>
            <Table className='w-full shadow-2xl' size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell className='text-xs font-semibold'>Timestamp</TableCell>
                        {selectedKeys.map(key => (<TableCell key={key} align='right' className='text-xs font-semibold whitespace-nowrap'>{key}</TableCell>))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {data.map((row, idx) => (
                        <TableRow key={idx}>
                            <TableCell className='whitespace-nowrap text-xs py-12'>{format(new Date(row.ts), 'yyyy.MM.dd.')}</TableCell>
                            {selectedKeys.map(key => (
                                <TableCell key={key} align='right' className='whitespace-nowrap text-xs tabular-nums'>
                                    {row[key] == null ? '-' : formatLabelValue(row[key], defaultLabelStyle())}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function DatePick(props) {
    return (
        <DatePicker
            className={props.className}
            label={props.label}
            margin='dense'
            value={props.value ?? undefined}
            onChange={e => props.onChange(e)}
            ampm={false}
            minDate={props.minDate}
            maxDate={props.maxDate}
            format='yyyy MMMM d.'
            cancelLabel='Back'
            okLabel='Ok'
            clearLabel='Clear'
            inputVariant='outlined'
            clearable
        />
    );
}
